import { useState } from "react";
import { format, addWeeks, addMonths } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { generateRandomToken, generateQRCodeDataURL } from "@/utils/qrCodeGenerator";
import { createAuthToken } from "@/services/tokenService";
import { createPDFPage, generatePDF } from "@/utils/pdfGenerator";
import { supabase } from "@/integrations/supabase/client";
import type { Group } from "@/types/groups";

interface QRCodeGeneratorProps {
  groupId?: string;
  group?: Group;
}

interface SurveyDates {
  firstSurvey: { label: string; date: Date };
  secondSurvey: { label: string; date: Date };
  thirdSurvey: { label: string; date: Date };
}

export const QRCodeGenerator = ({ groupId, group }: QRCodeGeneratorProps) => {
  const [qrCount, setQrCount] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Calculate all survey dates from group
  const getSurveyDates = (): SurveyDates | null => {
    if (!group?.first_possible_date || !group?.assigned_group) {
      return null;
    }

    const firstPossibleDate = new Date(group.first_possible_date);
    
    if (group.assigned_group === "intervention") {
      const fosStart = firstPossibleDate;
      const fosEnd = addWeeks(fosStart, 4);
      const followUp = addMonths(fosStart, 6);
      
      return {
        firstSurvey: { label: "Första FÖS-träff", date: fosStart },
        secondSurvey: { label: "Sista FÖS-träff", date: fosEnd },
        thirdSurvey: { label: "6 månader efter start", date: followUp },
      };
    } else {
      // Control: Theme meeting first, then FÖS after 4 weeks
      const themeDate = firstPossibleDate;
      const fosStart = addWeeks(firstPossibleDate, 4);
      const fosEnd = addWeeks(fosStart, 4);
      const followUp = addMonths(fosStart, 6);
      
      return {
        firstSurvey: { label: "Tematräff", date: themeDate },
        secondSurvey: { label: "Sista FÖS-träff", date: fosEnd },
        thirdSurvey: { label: "6 månader efter FÖS-start", date: followUp },
      };
    }
  };

  const surveyDates = getSurveyDates();
  
  // For QR code generation, we need the first meeting date
  const firstMeetingDate = surveyDates?.firstSurvey.date || null;
  const themeMeetingDate = group?.assigned_group === "control" && surveyDates 
    ? surveyDates.firstSurvey.date 
    : null;

  const handleGenerate = async () => {
    if (group && (!firstMeetingDate)) {
      toast({
        title: "Fel",
        description: "Gruppschemat måste accepteras innan QR-koder kan genereras",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGenerating(true);
      
      // Get current user info
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error("No authenticated user found");
      }
      
      const container = document.createElement("div");
      
      for (let i = 0; i < qrCount; i++) {
        const token = generateRandomToken();
        await createAuthToken(
          token, 
          firstMeetingDate || new Date(), 
          themeMeetingDate || new Date(),
          groupId
        );
        const qrCodeDataUrl = await generateQRCodeDataURL(token);
        const page = await createPDFPage(token, qrCodeDataUrl);
        container.appendChild(page);
      }

      document.body.appendChild(container);
      await generatePDF(container);
      document.body.removeChild(container);
      
      queryClient.invalidateQueries({ queryKey: ['qrCodes'] });
      
      toast({
        title: "Framgång",
        description: `Genererade ${qrCount} QR-kod${qrCount > 1 ? 'er' : ''} framgångsrikt`,
      });
    } catch (error) {
      console.error('Error generating QR codes:', error);
      toast({
        title: "Fel",
        description: error instanceof Error ? error.message : "Det gick inte att generera QR-koder. Försök igen.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Show group info with all survey dates
  const groupInfo = group && surveyDates && (
    <div className="mb-4 p-3 bg-muted rounded-lg text-sm space-y-2">
      <p><strong>Grupp:</strong> {group.name || group.id.slice(0, 8)}</p>
      <p><strong>Tilldelning:</strong> {group.assigned_group === "intervention" ? "Intervention" : "Kontroll"}</p>
      
      <div className="mt-3 pt-2 border-t border-border">
        <p className="font-medium mb-1">Enkätdatum:</p>
        <p><strong>1. {surveyDates.firstSurvey.label}:</strong> {format(surveyDates.firstSurvey.date, "yyyy-MM-dd")}</p>
        <p><strong>2. {surveyDates.secondSurvey.label}:</strong> {format(surveyDates.secondSurvey.date, "yyyy-MM-dd")}</p>
        <p><strong>3. {surveyDates.thirdSurvey.label}:</strong> {format(surveyDates.thirdSurvey.date, "yyyy-MM-dd")}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 bg-card p-6 rounded-lg shadow-sm">
      <h2 className="text-xl font-medium">Generera QR-koder</h2>
      
      {groupInfo}
      
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Antal QR-koder att generera:</p>
        <div className="flex items-center space-x-2">
          <Input
            type="number"
            min="1"
            value={qrCount}
            onChange={(e) => setQrCount(parseInt(e.target.value) || 1)}
            className="text-center"
          />
          <Button 
            onClick={handleGenerate} 
            className="flex-1"
            disabled={isGenerating || (group && !group.schedule_accepted_at)}
          >
            {isGenerating ? "Genererar..." : "Generera PDF"}
          </Button>
        </div>
      </div>
    </div>
  );
};
